import { isNativeDevice } from "@origin-digital/platform-helpers";
import { isOnServer } from "@origin-digital/tal-common";

/**
 * Single source of truth for every package's log-message tag.
 *
 * Before this existed, each package hand-typed its own bracketed prefix
 * inline in every `logger.error/warn/info/debug(...)` call, which drifted
 * into several inconsistent spellings per package over time (e.g. "mesh"
 * alone had five: "[mesh]", "[od/mesh]", "[od/mesh]:", "[od/mesh].",
 * "[@od/mesh]"). Import the relevant key from here instead of writing the
 * bracket string by hand, so every call site for a package shares one
 * definition and can't drift or get misspelled again.
 *
 * House style: "[@od/<name>]" — always @-scoped, never a trailing colon.
 */
export const LOG_TAGS = {
  bootloader: "[@od/bootloader]",
  braze: "[@od/braze]",
  eventRegistry: "[@od/event-reg]",
  mcappRegistry: "[@od/mcapp-reg]",
  mesh: "[@od/mesh]",
  nativeFeatures: "[@od/native-features]",
  originAuth: "[@od/origin-auth]",
  originChat: "[@od/chat]",
  survicate: "[@od/survicate]",
  talClient: "[@od/tal-client]",
} as const;

export type LogTagPackage = keyof typeof LOG_TAGS;

/**
 * Prefixes a package's LOG_TAG with the runtime context every logged message
 * should carry:
 *   - "[server]" when running inside tal-core's SSR environment
 *     (`@origin-digital/tal-common`'s isOnServer() — tal-core evaluates the
 *     browser bundle inside JSDOM, so `window` genuinely exists there;
 *     isNativeDevice()'s `typeof window !== "undefined"` guard alone can't
 *     detect this case, it would fall through to checking JSDOM's fake
 *     `navigator.userAgent` instead) — checked first, since it takes
 *     priority over the native/web distinction below (SSR isn't running on
 *     either device).
 *   - "[native]" when running inside the odin-mobile-app webview.
 *   - "[web]" otherwise.
 * Computed once, synchronously, so each package's logTag.ts picks this up at
 * its own module-load time with no dependency on any later runtime
 * configuration step.
 *
 * Use at the point each package defines its own LOG_TAG, e.g.:
 *   export const LOG_TAG = withDeviceTag(LOG_TAGS.bootloader);
 * — every `logger.error(`${LOG_TAG} ...`)` call site then carries the tag
 * automatically, with no other call sites needing to change.
 */
export const withDeviceTag = (tag: string): string => {
  if (isOnServer()) {
    return `[server] ${tag}`;
  }
  return `${isNativeDevice() ? "[native]" : "[web]"} ${tag}`;
};
